import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import random01 from '../assets/images/random_01.jpg';
import usePricing from '../hooks/usePricing';
import './Pricing.css';

const STUDENT_NOTES = (hourlyRate) => [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    title: 'Pay Remaining Payment to Instructor',
    text: 'After the upfront booking fee is paid online, the remaining balance of your lesson package is paid directly to your instructor after your first lesson.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
    title: 'Standard Lesson Rate',
    text: `The standard lesson rate is £${hourlyRate} per hour. After the initial trial lesson, any additional hours taught by the instructor will be charged at the standard rate unless included in a booked package.`,
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4"/>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
    title: 'Refund Policy',
    text: `Refunds can be requested immediately after your first 2-hour session if you decide not to continue. Any completed lesson hours will be charged at the £${hourlyRate} standard rate, and the remaining unused hours from your package will be refunded.`,
  },
];

const INSTRUCTOR_NOTES = (hourlyRate) => [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
      </svg>
    ),
    title: 'Zero Fees',
    text: 'No franchise fees, no monthly fees, no registration fees — ever. Fast Track keeps only the upfront booking fee.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2"/>
        <line x1="2" y1="10" x2="22" y2="10"/>
      </svg>
    ),
    title: 'Direct Payment',
    text: 'You collect the remaining balance directly from the student — in cash or bank transfer, whichever suits you both.',
  },

  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    title: 'Standard Rate After Trial',
    text: `After the trial lesson, all additional hours will be charged at the standard rate of £${hourlyRate} per hour.`,
  },
];

export default function Pricing() {
  const [activeTab, setActiveTab] = useState('students');
  const { pricing, packages: PACKAGES } = usePricing();
  const maxSavings = Math.max(...PACKAGES.map(p => p.savings || 0));
  const hourlyRate = pricing?.hourly_rate || 37;

  return (
    <div className="pr-page">

      {/* ── Hero ── */}
      <section className="pr-hero">
        <div className="pr-hero-bg-dots" aria-hidden="true" />
        <div className="pr-hero-inner">
          <span className="pr-eyebrow">Transparent Pricing</span>
          <h1>Simple, Honest Lesson Packages</h1>
          <p>
            No hidden fees, no nasty surprises. Pay a small upfront fee online to secure your
            booking — your instructor collects the rest. Standard rate is <strong>£{hourlyRate}/hour</strong>.
          </p>
          <p className="pr-hero-punchline">
            Manual and automatic driving lessons are available across major UK test centre areas. Simply submit your booking request and we will match you with a suitable instructor in your area.
          </p>
          <div className="pr-hero-badges">
            <span className="pr-hero-badge">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
              DVSA-Approved Instructors
            </span>
            <span className="pr-hero-badge">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
              Secure Online Payment
            </span>
            <span className="pr-hero-badge">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
              Cancel After First Session
            </span>
          </div>
        </div>
      </section>

      {/* ── Stats bar ── */}
      <div className="pr-stats-bar">
        <div className="pr-stats-inner">
          <div className="pr-stat">
            <span className="pr-stat-num">£{hourlyRate}</span>
            <span className="pr-stat-label">Per Hour Rate</span>
          </div>
          <div className="pr-stat">
            <span className="pr-stat-num">4</span>
            <span className="pr-stat-label">Packages</span>
          </div>
          <div className="pr-stat">
            <span className="pr-stat-num">£{maxSavings}</span>
            <span className="pr-stat-label">Max Savings</span>
          </div>
          <div className="pr-stat">
            <span className="pr-stat-num">0</span>
            <span className="pr-stat-label">Hidden Fees</span>
          </div>
        </div>
      </div>

      {/* ── How payment works ── */}
      <section className="pr-section pr-section--white">
        <div className="pr-container">
          <div className="pr-section-head">
            <span className="pr-eyebrow-sm">How It Works</span>
            <h2>Simple Two-Step Payment</h2>
            <p className="pr-section-intro">
              We keep booking straightforward. Pay a small fee online to confirm your place —
              your instructor collects the rest directly.
            </p>
          </div>

          <div className="pr-steps">
            <div className="pr-step">
              <div className="pr-step-num">1</div>
              <div className="pr-step-body">
                <h3>Pay Upfront Fee Online</h3>
                <p>Secure your booking by paying the upfront fee through our Stripe-powered checkout. This confirms your slot.</p>
              </div>
            </div>
            <div className="pr-step-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="pr-step">
              <div className="pr-step-num">2</div>
              <div className="pr-step-body">
                <h3>Pay Remaining to Instructor</h3>
                <p>After your first lesson, pay the outstanding balance directly to your instructor — cash or bank transfer. Fast, simple, no middleman.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Cards ── */}
      <section className="pr-section pr-section--grey">
        <div className="pr-container">
          <div className="pr-section-head">
            <span className="pr-eyebrow-sm">Lesson Packages</span>
            <h2>Choose Your Package</h2>
            <p className="pr-section-intro">
              All packages include a fully qualified DVSA-approved instructor, a modern dual-controlled vehicle, and flexible scheduling.
            </p>
          </div>

          <div className="pr-cards">
            {PACKAGES.map((pkg) => (
              <div key={pkg.id} className={`pr-card pr-card--${pkg.colour}${pkg.tag ? ' pr-card--featured' : ''}`}>
                {pkg.tag && (
                  <div className="pr-card-tag">{pkg.tag}</div>
                )}

                <div className="pr-card-header">
                  <div className="pr-card-title-row">
                    <h3 className="pr-card-name">{pkg.name}</h3>
                    <span className="pr-card-hours">{pkg.hours} hrs</span>
                  </div>
                  <p className="pr-card-desc">{pkg.desc}</p>
                </div>

                <div className="pr-card-price-block">
                  <div className="pr-card-price">
                    <span className="pr-card-amount">£{pkg.packagePrice}</span>
                    <span className="pr-card-standard">
                      <s>£{pkg.standardPrice}</s>
                    </span>
                  </div>
                  <span className="pr-card-save-badge">Save £{pkg.savings} ({pkg.discount}% off)</span>
                </div>

                <div className="pr-card-split">
                  <div className="pr-card-split-row">
                    <span className="pr-split-label">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="pr-split-icon pr-split-icon--online">
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"/>
                      </svg>
                      Pay Online Now
                    </span>
                    <span className="pr-split-amount pr-split-amount--online">£{pkg.upfront}</span>
                  </div>
                  <div className="pr-card-split-divider" />
                  <div className="pr-card-split-row">
                    <span className="pr-split-label">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="pr-split-icon pr-split-icon--instructor">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                      </svg>
                      Instructor Fee
                    </span>
                    <span className="pr-split-amount pr-split-amount--instructor">
                      {pkg.remaining === 0 ? <span className="pr-split-free">£0</span> : `£${pkg.remaining}`}
                    </span>
                  </div>
                </div>

                <Link to={`/book-lesson?package=${pkg.id}`} className={`pr-card-cta${pkg.tag ? ' pr-card-cta--featured' : ''}`}>
                  {pkg.id === 'trial' ? 'Start My Trial' : `Book ${pkg.name}`}
                </Link>
              </div>
            ))}
          </div>

          <p className="pr-disclaimer">
            Standard rate: £{hourlyRate} per hour. Package prices represent a saving on standard rate.
            All payments are processed securely via Stripe.
          </p>
        </div>
      </section>

      {/* ── Important Information Tabs ── */}
      <section className="pr-section pr-section--white" id='imp-info'>
        <div className="pr-container">
          <div className="pr-section-head">
            <span className="pr-eyebrow-sm">Good to Know</span>
            <h2>Important Information</h2>
          </div>

          <div className="pr-tabs">
            <button
              className={`pr-tab${activeTab === 'students' ? ' pr-tab--active' : ''}`}
              onClick={() => setActiveTab('students')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              For Students
            </button>
            <button
              className={`pr-tab${activeTab === 'instructors' ? ' pr-tab--active' : ''}`}
              onClick={() => setActiveTab('instructors')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M2 20c0-4 4-7 10-7s10 3 10 7"/>
                <path d="M15.5 3.5L17 2l1.5 1.5L17 5l-1.5-1.5z"/>
              </svg>
              For Instructors
            </button>
          </div>

          <div className="pr-tab-panel">
            {activeTab === 'students' && (
              <>
                <div className="pr-info-grid">
                  {STUDENT_NOTES(hourlyRate).map((note, i) => (
                    <div key={i} className="pr-info-card">
                      <div className="pr-info-icon">{note.icon}</div>
                      <h4>{note.title}</h4>
                      <p>{note.text}</p>
                    </div>
                  ))}
                </div>
                <div className="pr-refund-banner">
                  <div className="pr-refund-banner-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.39"/></svg>
                  </div>
                  <div className="pr-refund-banner-text">
                    <strong>Need a refund?</strong> Refunds are available after your first 2-hour session, before any further lessons begin.
                  </div>
                  <Link to="/refund-request" className="pr-refund-banner-btn">Submit a Refund Request →</Link>
                </div>
              </>
            )}
            {activeTab === 'instructors' && (
              <div className="pr-info-grid">
                {INSTRUCTOR_NOTES(hourlyRate).map((note, i) => (
                  <div key={i} className="pr-info-card">
                    <div className="pr-info-icon pr-info-icon--navy">{note.icon}</div>
                    <h4>{note.title}</h4>
                    <p>{note.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="pr-cta-section" style={{ backgroundImage: `linear-gradient(135deg, rgba(10,31,78,0.85) 0%, rgba(16,67,160,0.80) 100%), url(${random01})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="pr-cta-inner">
          <div className="pr-cta-bg-shape" aria-hidden="true" />
          <span className="pr-eyebrow pr-eyebrow--light">Ready to Drive?</span>
          <h2>Book Your First Lesson Today</h2>
          <p>Join hundreds of students across the UK who've passed their test with Fast Track.</p>
          <div className="pr-cta-btns">
            <Link to="/book-lesson" className="pr-cta-btn-primary">Book Now</Link>
            <Link to="/contact" className="pr-cta-btn-outline">Ask a Question</Link>
          </div>
        </div>
      </section>

    </div>
  );
}
